import type { ActivationFlowType, ClaimOrderSummaryForFrontend } from "../api/verifyCustomer";

/**
 * Customer verification result handling, shared by the guide's two verification paths: PIN
 * (`verifyCustomer`) and OTP (`verifyPasscodeForVerification`). Both routes return the same shape and
 * resolve the guide identically, so the handling lives here once instead of once per `onNext` case.
 */

/** The subset of a verification response this resolver reads. Both routes' results satisfy it. */
export interface CustomerVerificationApiResult {
    accountMdns?: string[];
    activationFlowType?: ActivationFlowType;
    claimSummaries: ClaimOrderSummaryForFrontend[];
    token?: string;
    errorCode?: string;
}

/** An account MDN as displayed to the expert — masked for the UI, raw value kept for later requests. */
export interface AccountMdn {
    masked: string;
    value: string;
}

export interface ResolvedCustomerVerification {
    /** Patch to merge into the card's `customerState`. */
    customerPatch: {
        accountMdns: AccountMdn[];
        activationFlowType?: ActivationFlowType;
        claimSummaries: ClaimOrderSummaryForFrontend[];
    };
    /** The guide step to navigate to. */
    nextStep: string;
    /** The verification token, present only when verification succeeded. */
    token?: string;
}

/** Dedicated guide steps for the error codes the verification routes can return. */
const NEXT_STEP_BY_ERROR_CODE: Record<string, string> = {
    SUBSCRIBER_NOT_FOUND: "subscriberNotFound",
    UNSUPPORTED_DEVICE_TYPE: "unsupportedDevice",
};

/**
 * Resolve a verification response into the customer-state patch, the next guide step, and the token to
 * store: a token means success, a known error code routes to its dedicated step, and anything else
 * (an unknown code, or no code at all) falls through to the generic failure step.
 */
export function resolveCustomerVerification(result: CustomerVerificationApiResult): ResolvedCustomerVerification {
    const { accountMdns, activationFlowType, claimSummaries, errorCode, token } = result;

    const customerPatch = {
        // MDNs are masked for display; the raw value is kept for the follow-up passcode request.
        // `?? []` because the declared response type doesn't bind a real 200 body: the callers'
        // consumers (dropdown overrides) assume an array, so never hand them `undefined`.
        accountMdns: (accountMdns ?? []).map((mdn) => ({ masked: `****${mdn.slice(-4)}`, value: mdn })),
        activationFlowType,
        claimSummaries,
    };

    if (token) {
        return { customerPatch, nextStep: "success", token };
    }

    return { customerPatch, nextStep: (errorCode && NEXT_STEP_BY_ERROR_CODE[errorCode]) || "failure" };
}
